import React from 'react';

interface CalendarDayIconProps {
  size?: number;
  strokeColor?: string;
  numberColor?: string;
  day?: number | string;
  strokeWidth?: number;
  // When true, render a checkmark inside the calendar instead of the day number.
  check?: boolean;
  // When true, render an ✕ inside the calendar (uses `numberColor` for the ✕).
  cancel?: boolean;
}

// Minimalist calendar-day icon drawn entirely with SVG primitives.
// Everything is authored against a fixed 48×48 viewBox, so changing `size`
// scales the whole thing proportionally and stays resolution independent.
const CalendarDayIcon: React.FC<CalendarDayIconProps> = ({
  size = 48,
  strokeColor = '#2E3748',
  numberColor = '#2ECC71',
  day = 1,
  strokeWidth = 2.75,
  check = false,
  cancel = false,
}) => {
  // Six evenly spaced binding tabs across the calendar body (x: 6 → 42).
  const tabXs = [9, 15, 21, 27, 33, 39];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`יום ${day}`}
    >
      <g
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {/* Calendar body */}
        <rect x={6} y={9} width={36} height={33} rx={4} ry={4} />

        {/* Binding tabs extending above the top edge */}
        {tabXs.map((x) => (
          <line key={x} x1={x} y1={4.5} x2={x} y2={13} />
        ))}

        {/* Top divider just below the tabs */}
        <line x1={8} y1={16.5} x2={40} y2={16.5} />
      </g>

      {cancel ? (
        // Centered ✕ inside the calendar body.
        <g
          stroke={numberColor}
          strokeWidth={3}
          strokeLinecap="round"
          fill="none"
        >
          <line x1={18.5} y1={24} x2={29.5} y2={35} />
          <line x1={29.5} y1={24} x2={18.5} y2={35} />
        </g>
      ) : check ? (
        // Centered checkmark inside the calendar body.
        <path
          d="M17 30 L21.5 34.5 L32 24"
          stroke={numberColor}
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      ) : (
        // Centered day number (slightly above the mid-line for room below).
        <text
          x={24}
          y={31}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif"
          fontSize={19}
          fontWeight={600}
          fill={numberColor}
        >
          {day}
        </text>
      )}
    </svg>
  );
};

export default CalendarDayIcon;
